import React from 'react';
import { getClaimRequirements } from '../data/claimRequirements';
import { wordsFor } from '../data/craftTerms';   // section 807: the words of the claim's division

// Atomic one-question / one-answer capture boxes for the requirement elements that need
// direct capture: presentment, proof of delivery, and the narrative-content items.
// Requirements-driven — renders only the boxes the resolved agreement requires, and
// nothing at all when the agreement is uncatalogued ("if nothing, then nothing").

const QUESTION = {
  presentedTo: {
    q: 'Who are you presenting this claim to?',
    help: 'Your immediate supervisor, or the carrier officer authorized to receive claims.',
    type: 'text', placeholder: 'Name and title of the supervisor / officer',
  },
  proofOfDelivery: {
    q: 'How is the claim being delivered, and what is the proof?',
    help: 'A claim’s timeliness is defended by proof of delivery — keep the receipt or tracking number.',
    type: 'delivery',
  },
  detailsOfWork: {
    q: 'What work was performed, or what opportunity was denied?',
    help: 'The specific work at issue. A suggested draft can be offered here.',
    type: 'textarea', placeholderKey: 'detailsOfWorkExample',
  },
  reasonsSupporting: {
    q: 'What are the reasons supporting the claim?',
    help: 'Why this is a violation and why payment is warranted. A suggested draft can be offered here.',
    type: 'textarea', placeholderKey: 'reasonsSupportingExample',
  },
};

// Ruling 325 (section 761): first-class mail is the member's option too. An e-mailed receipt may be challenged; the agreement's own terms govern.
const DELIVERY_METHODS = ['Hand delivery with signed receipt', 'Certified mail (return receipt)', 'First-class mail', 'Electronic mail', 'Other'];

// `omit` (ruling 261): question keys a caller does not show. Absent, nothing is omitted - the component is
// exactly what it was before 261 for every caller that does not pass it (NewClaim does not).
export default function ClaimBasisBoxes({ agreement, form, onSet, omit }) {
  const req = getClaimRequirements(agreement);
  if (!req) return null;

  const fields = [];
  [...req.eligibility, ...req.payable].forEach(el => {
    const key = Array.isArray(el.field) ? null : el.field;
    if (key && QUESTION[key] && !fields.includes(key) && !(Array.isArray(omit) && omit.includes(key))) fields.push(key);
  });
  if (fields.length === 0) return null;

  return (
    <div className="card" style={{ borderLeft: '4px solid #2E6B4A' }}>
      <div className="card-header">
        <div>
          <div className="card-title">Claim Basis &amp; Presentment</div>
          <div className="card-subtitle" style={{ color: '#E8A020', fontSize: 11, fontWeight: 600 }}>
            One question per box — what makes the claim payable and defensible under {req.claimsRule}.
          </div>
        </div>
      </div>

      {fields.map(key => {
        // Ruling 262: "Who are you presenting this claim to?" shows the officer THIS agreement designates.
        const Q = key === 'presentedTo' && req.presentmentOfficer
          ? { ...QUESTION[key], help: 'The agreement designates ' + req.presentmentOfficer + '. Give that officer\u2019s name and title.' }
          : QUESTION[key];
        return (
          <div className="form-group" key={key}>
            <label className="form-label">{Q.q}</label>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>{Q.help}</div>
            {Q.type === 'text' && (
              <input aria-label="Your answer to the question above" className="form-input" placeholder={Q.placeholderKey ? wordsFor((form || {}).craft)[Q.placeholderKey] : Q.placeholder}
                value={form?.[key] || ''} onChange={e => onSet(key, e.target.value)} />
            )}
            {Q.type === 'textarea' && (
              <textarea aria-label="Your answer to the question above" className="form-textarea" rows={3} placeholder={Q.placeholderKey ? wordsFor((form || {}).craft)[Q.placeholderKey] : Q.placeholder}
                value={form?.[key] || ''} onChange={e => onSet(key, e.target.value)} />
            )}
            {Q.type === 'delivery' && (
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <select aria-label="Delivery method" className="form-select" value={form?.proofOfDeliveryMethod || ''}
                  onChange={e => onSet('proofOfDeliveryMethod', e.target.value)}>
                  <option value="">Delivery method…</option>
                  {DELIVERY_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <input aria-label="Receipt / tracking number" className="form-input" placeholder="Receipt / tracking number"
                  value={form?.proofOfDelivery || ''} onChange={e => onSet('proofOfDelivery', e.target.value)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
